import {
  Grid,
  Skeleton,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Theme,
} from '@mui/material';
import { Form, Formik , FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { ReportsRightLayout } from './index';
import { MFSubmitButton } from '../../lib/formik';
import { Location } from 'history';
import MFSelectField from '../../lib/formik/SelectField';
import dayjs from 'dayjs';
import { useState , useRef } from 'react';
import { DayJsDatePicker } from '../onboarding/newDatePicker';
import * as Yup from 'yup';
import { RootStateType } from '../../redux-store/reducers';
import { getCGInvestorReport } from '../../redux-store/actions/reports';
import { reportDownload } from '../Reports/report-utils';
import { CONTENT_TYPE } from '../../utils/constant';
import { SuccessIcon, FailureIcon } from '../../assets/SvgIcons';

const durationOptions = [
  { key: 'Previous FY', value: 'previous-fy' },
  { key: 'Current FY', value: 'current-fy' },
  { key: 'Custom Date', value: 'custom' },
]

const downloadOptions = [
  { key: 'PDF', value: 'pdf' },
  { key: 'Excel', value: 'excel' },
]

const shareViaOptions = [
  { key: 'Email', value: 'email' },
  { key: 'Download', value: 'download' },
]

interface Values {
  duration: string;
  downloadOption: string;
  startDate: string;
  endDate: string;
  shareVia: string;
}

function getCurrentFinancialYear() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  let startYear, endYear;

  if (currentMonth >= 3) { // Financial year starts in April
    startYear = currentYear;
    endYear = currentYear + 1;
  } else {
    startYear = currentYear - 1;
    endYear = currentYear;
  }

  return { start: startYear, end: endYear };
}

// Function to get the previous financial year range
function getPreviousFinancialYear() {
  const { start, end } = getCurrentFinancialYear();
  return { start: start - 1, end: end - 1 };
}

const previousFY = getPreviousFinancialYear();
const currentFY = getCurrentFinancialYear();

const CGStatement = ({
  location,
  handleSideSectionDisabled
}: {
  location: Location<{ investorId: string; investorName: string }>;
  handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element => {
  const dispatch = useDispatch();
  const currentDate = dayjs();
  const { iwappRmId = "", investorClientId = "" } = useSelector((state: RootStateType) => state.id);
  const { investorName = "" } = useSelector((state: RootStateType) => state.id);
  const { panNo = "" } = useSelector((state: RootStateType) => state.id);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState<boolean>(false)
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const todayDate = new Date();
  const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");
  const formikRef = useRef<FormikProps<Values>>(null);

  const initialValues = {
    duration: '',
    downloadOption: '',
    startDate: formattedBusinessDate,
    endDate: formattedBusinessDate,
    shareVia: '',
  }

  const cgInvestorReportValidationSchema = Yup.object().shape({
    duration: Yup.string().required('Please select duration'),
    downloadOption: Yup.string().required('Please select download option'),
    shareVia: Yup.string().required('Please select share or download'),
    startDate: Yup
      .date()
      .when('duration', {
        is: (duration: string) => duration === 'custom',
        then: Yup
          .date()
          .required('From Date is required')
          .typeError('From Date is required')
          .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
          .max(formattedBusinessDate, "From Date should not be greater than system's date")
          .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
            const { endDate } = this.parent;
            return !endDate || !value || value <= endDate;
          })
      }),
    endDate: Yup
      .date()
      .when('duration', {
        is: (duration: string) => duration === 'custom',
        then: Yup
          .date()
          .required('From Date is required')
          .typeError('To Date is required')
          .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
          .max(formattedBusinessDate, "To Date should not be greater than system's date")
          .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
            const { startDate } = this.parent;
            return !startDate || !value || value >= startDate;
          }),
      }),
  })

  const handleSubmit = async ({
    duration,
    downloadOption,
    startDate,
    endDate,
    shareVia
  }: {
    duration: string;
    downloadOption: string;
    startDate: string;
    endDate: string;
    shareVia: string;
  }) => {
    try {
      setIsDownloading(true)
      handleSideSectionDisabled(true)
      const payload = {
        clientCode: `${investorClientId}`,
        investorName: `${investorName}`,
        panNo: `${panNo}`,
        rmId: `${iwappRmId}`,
        start: duration === 'current-fy' ? `01/04/${currentFY.start}` : duration === 'previous-fy' ? `01/04/${previousFY.start}` : dayjs(startDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
        end: duration === 'current-fy' ? dayjs(formattedBusinessDate, "YYYY-MM-DD").format("DD/MM/YYYY") : duration === 'previous-fy' ? `31/03/${previousFY.end}` : dayjs(endDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
        isPDF: (downloadOption ?? "").toLowerCase() === 'pdf' ? true : false,
        shareVia: (shareVia ?? "").toLowerCase() === 'email' ? 'email' : 'download',
      }
      const response = await (dispatch(getCGInvestorReport(payload)) as unknown as Promise<any>)
      if (shareVia?.toLowerCase() === 'download') {
        if (response.type) {
          setIsSuccessDialogOpen(true)
          reportDownload(response as unknown as Blob, `CGStatementInvestorReport.${response.type === CONTENT_TYPE.PDF ? 'pdf' : 'xlsx'}`);
        }
        else {
          setIsErrorDialogOpen(true)
          setErrorMessage('File download failed. Please try again.')
        }
      }
      else if (shareVia?.toLowerCase() === 'email') {
        setIsSuccessDialogOpen(true)
      }

    } catch (error) {
      console.log("Error: ", error)
    } finally {
      setIsDownloading(false)
      handleSideSectionDisabled(false)
    }
  }

  return (
    <ReportsRightLayout>
      <Typography
        variant='h2'
        sx={{
          color: 'text.primary',
          lineHeight: '25px',
          letterSpacing: '0.01px',
          mb: { xs: '20px', sm: '40px' }
        }}>
        Capital Gain Statement
      </Typography>

      <Formik
        // validationSchema={validationSchemaCgStatement}
        validationSchema={cgInvestorReportValidationSchema}
        // initialValues={{
        //   duration: options[0].value,
        //   fromDate: new Date(),
        //   toDate: new Date(),
        //   fileType: 'pdf',
        // }}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        innerRef={formikRef}
      >
        {({ values, setFieldValue, setFieldError }) => {
          return (
            <Form>

              {/* <ReportSectionContainer>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                    letterSpacing: '0.01em',
                    pt: 1,
                    pb: 1,
                  }}>
                  Choose duration of statement -
                </Typography>
                <Box>
                  <FormControl sx={{ width: '100%' }}>
                    <RadioGroup
                      name={'duration'}
                      value={values.duration}
                      onChange={(event) => {
                        setFieldValue('duration', event.currentTarget.value);
                      }}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: ['1fr 1fr', '1fr 1fr 1fr'],
                        gridColumnGap: 8,
                        gridRowGap: '1rem',
                      }}>
                      {options.map(({ label, value }) => (
                        <FormControlLabel
                          key={value}
                          value={value}
                          control={<BpRadio />}
                          label={label}
                        />
                      ))}
                    </RadioGroup>
                    {values.duration === 'custom' && (
                      <>
                        <TextDatePicker
                          label={'From Date'}
                          inputLabelStyles={{
                            transform: 'unset',
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'rgba(0,0,0,0.7)',
                          }}
                          applyLabelStyles={true}
                          placeholder={'Enter Net worth as on Date'}
                          name={`fromDate`}
                        />
                        <TextDatePicker
                          label={'To Date'}
                          inputLabelStyles={{
                            transform: 'unset',
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'rgba(0,0,0,0.7)',
                          }}
                          applyLabelStyles={true}
                          placeholder={'Enter Net worth as on Date'}
                          name={`toDate`}
                        />
                      </>
                    )}
                  </FormControl>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                    letterSpacing: '0.01em',
                    pt: 2,
                    pb: 1,
                  }}>
                  Report download options
                </Typography>
  
                <FormControl>
                  <RadioGroup
                    name={'fileType'}
                    value={values.fileType}
                    onChange={(event) => {
                      setFieldValue('fileType', event.currentTarget.value);
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                    }}>
                    <FormControlLabel value="pdf" control={<BpRadio />} label="PDF" />
                    <FormControlLabel value="xlsx" control={<BpRadio />} label="Excel" />
                  </RadioGroup>
                </FormControl>
              </ReportSectionContainer>
              <Stack justifyContent="center" alignItems="center" sx={{ m: 2 }}>
                <MFSubmitButton
                  label="Download"
                />
              </Stack> */}
              <Grid container rowSpacing={2} >
                <Grid item container columnSpacing={3}>
                  <Grid item xs={12} lg={6}>
                    {isNavbarInfoLoading && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {!isNavbarInfoLoading && <MFSelectField
                      name={'duration'}
                      label="Select Duration"
                      items={durationOptions}
                      value={values.duration}
                      onChange={(e) => {
                        setFieldValue('duration', e.target.value);
                        setFieldValue("startDate", formattedBusinessDate)
                        setFieldValue("endDate", formattedBusinessDate)
                        setFieldError("startDate", "")
                        setFieldError("endDate", "")
                      }}
                      disabled={isDownloading}
                    />}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {isNavbarInfoLoading && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {!isNavbarInfoLoading && <MFSelectField
                      name={'downloadOption'}
                      label="Select Download Option"
                      items={downloadOptions}
                      value={values.downloadOption}
                      onChange={(e) => {
                        setFieldValue('downloadOption', e.target.value);
                      }}
                      disabled={isDownloading}
                    />}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {(isNavbarInfoLoading) && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {(!isNavbarInfoLoading) && <MFSelectField
                      name={'shareVia'}
                      label="Select Share or Download"
                      items={shareViaOptions}
                      value={values.shareVia}
                      onChange={(e) => {
                        setFieldValue('shareVia', e.target.value);
                      }}
                      disabled={isDownloading}
                    />}
                  </Grid>
                </Grid>
                {values.duration === 'custom' && (
                  <Grid item container columnSpacing={3}>
                    <Grid item xs={12} lg={6}>
                      <DayJsDatePicker
                        id=""
                        name='startDate'
                        label="From Date"
                        maxDate={new Date(formattedBusinessDate)}
                        disabled={isDownloading}
                        currentDate={formattedBusinessDate}
                      />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <DayJsDatePicker
                        id=""
                        name='endDate'
                        label="To Date"
                        minDate={new Date(values.startDate)}
                        maxDate={new Date(formattedBusinessDate)}
                        disabled={isDownloading}
                        currentDate={formattedBusinessDate}
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid item container columnSpacing={3} justifyContent={'flex-end'} sx={{ mt: 2 }}>
                  <Grid item xs={12} lg={6}>
                    {isNavbarInfoLoading && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {!isNavbarInfoLoading && <MFSubmitButton
                      label="Proceed"
                      loadingEnable={isDownloading}
                    />}
                  </Grid>
                </Grid>

              </Grid>
            </Form>
          )
        }}
      </Formik>
      <Dialog
        aria-labelledby="success-dialog"
        open={isSuccessDialogOpen}
        PaperProps={{
          sx: (theme: Theme) => ({
            maxWidth: '550px',
            width: "100%",
            borderRadius: "12px",
            padding: "24px",
            backgroundColor: theme.palette.common.white
          }),
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            rowGap: "12px",
            padding: "15px",
            overflow: "hidden"
          }}
        >
          <SuccessIcon />
          <Typography
            variant="h2"
            sx={(theme: Theme) => ({
              lineHeight: "25px",
              letterSpacing: "0.01px",
              color: theme.palette.text.primary
            })}
          >
            Success!
          </Typography>
          <Typography
            variant="subtitle_400"
            sx={(theme: Theme) => ({
              lineHeight: "22px",
              letterSpacing: "0.02px",
              color: theme.palette.text.secondary,
              textAlign: "center",
              paddingLeft: "10px",
              paddingRight: "10px"
            })}
          >
            {formikRef?.current?.values?.shareVia?.toLowerCase() === 'email' ? 'The statement has been  shared successfully.' : 'The statement has been downloaded successfully.'}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "0px",
            marginTop: "40px",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={() => setIsSuccessDialogOpen(false)}
            sx={(theme: Theme) => ({
              ...theme.typography.body_500,
              fontWeight: 600,
              lineHeight: "20px",
              color: theme.palette.common.white,
              borderRadius: "4px",
              padding: "13px 20px",
              backgroundColor: theme.palette.primary[500]
            })}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        aria-labelledby="error-dialog"
        open={isErrorDialogOpen}
        PaperProps={{
          sx: (theme: Theme) => ({
            maxWidth: '550px',
            width: "100%",
            borderRadius: "12px",
            padding: "24px",
            backgroundColor: theme.palette.common.white
          }),
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            rowGap: "12px",
            padding: "15px",
            overflow: "hidden"
          }}
        >
          <FailureIcon width="35" height="35" />
          <Typography
            variant="h2"
            sx={(theme: Theme) => ({
              lineHeight: "25px",
              letterSpacing: "0.01px",
              color: theme.palette.text.primary
            })}
          >
            Error!
          </Typography>
          <Typography
            variant="subtitle_400"
            sx={(theme: Theme) => ({
              lineHeight: "22px",
              letterSpacing: "0.02px",
              color: theme.palette.text.secondary,
              textAlign: "center",
              paddingLeft: "10px",
              paddingRight: "10px"
            })}
          >
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "0px",
            marginTop: "40px",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={() => setIsErrorDialogOpen(false)}
            sx={(theme: Theme) => ({
              ...theme.typography.body_500,
              fontWeight: 600,
              lineHeight: "20px",
              color: theme.palette.common.white,
              borderRadius: "4px",
              padding: "13px 20px",
              backgroundColor: theme.palette.primary[500]
            })}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </ReportsRightLayout>
  );
};

export default CGStatement;
